import type { ShapeStyle, Point } from '../../state/types';
import { Ellipse } from '../../shapes/Ellipse';
import { RECOGNITION_CONFIG } from '../config';
import type { BoundingBox } from '../geometry';
import type { RecognitionResult } from '../types';

export const recognizeEllipse = (
  points: Point[],
  bounds: BoundingBox,
  style?: Partial<ShapeStyle>
): RecognitionResult => {
  const rx = bounds.width / 2;
  const ry = bounds.height / 2;
  const center = bounds.center;

  if (rx < RECOGNITION_CONFIG.ellipseMinRadius || ry < RECOGNITION_CONFIG.ellipseMinRadius) {
    return { type: 'ellipse', confidence: 0 };
  }

  let totalDeviation = 0;
  for (const p of points) {
    const nx = (p.x - center.x) / rx;
    const ny = (p.y - center.y) / ry;
    const dist = Math.sqrt(nx * nx + ny * ny);
    totalDeviation += Math.abs(1 - dist);
  }

  const avgDeviation = totalDeviation / points.length;
  let confidence = Math.max(0, 1 - avgDeviation * RECOGNITION_CONFIG.ellipseDeviationWeight);

  const quadrants = [false, false, false, false];
  for (const p of points) {
    if (p.x >= center.x && p.y < center.y) quadrants[0] = true;
    if (p.x > center.x && p.y >= center.y) quadrants[1] = true;
    if (p.x <= center.x && p.y > center.y) quadrants[2] = true;
    if (p.x < center.x && p.y <= center.y) quadrants[3] = true;
  }
  const quadrantCount = quadrants.filter(Boolean).length;
  if (quadrantCount === 4) {
    confidence = Math.min(1, confidence + RECOGNITION_CONFIG.ellipseQuadrantBonus);
  }

  if (confidence > RECOGNITION_CONFIG.ellipseConfidenceThreshold) {
    const padding = RECOGNITION_CONFIG.ellipsePadding;
    const shape = new Ellipse(
      { x: bounds.minX - padding / 2, y: bounds.minY - padding / 2 },
      { width: bounds.width + padding, height: bounds.height + padding },
      style
    );
    return {
      type: 'ellipse',
      confidence: Math.min(confidence, RECOGNITION_CONFIG.ellipseMaxConfidence),
      shape,
    };
  }

  return { type: 'ellipse', confidence };
};
